import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";

/* ============================
   📦 CREATE PRODUCT
============================ */
export const createProduct = createAsyncThunk(
  "product/create",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/product/create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ============================
   📦 GET ALL PRODUCTS
============================ */
export const fetchProducts = createAsyncThunk(
  "product/fetchAll",
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/product/all", { params });
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ============================
   🏪 GET VENDOR PRODUCTS
   (Pagination + Search)
============================ */
export const fetchVendorProducts = createAsyncThunk(
  "product/fetchVendorProducts",
  async ({ vendorId, params = {} }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/product/vendor/${vendorId}`,
        { params }
      );

      return {
        products: data.data,
        pagination: data.pagination,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ============================
   🔍 GET SINGLE PRODUCT
============================ */
export const fetchSingleProduct = createAsyncThunk(
  "product/fetchSingle",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/product/single/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ============================
   ✏️ UPDATE PRODUCT
============================ */
export const updateProduct = createAsyncThunk(
  "product/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(
        `/product/update/${id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ============================
   🗑️ DELETE PRODUCT
============================ */
export const deleteProduct = createAsyncThunk(
  "product/delete",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/product/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

/* ============================
   🛒 PRODUCT SLICE
============================ */
const productSlice = createSlice({
  name: "product",

  initialState: {
    products: [],
    vendorProducts: [],
    vendorPagination: null,

    selectedProduct: null,
    loading: false,
    error: null,
    success: false,
  },

  reducers: {
    clearProductState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ===== CREATE ===== */
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.products.unshift(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== GET ALL ===== */
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== GET VENDOR PRODUCTS ===== */
      .addCase(fetchVendorProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVendorProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.vendorProducts = action.payload.products;
        state.vendorPagination = action.payload.pagination;
      })
      .addCase(fetchVendorProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== GET SINGLE ===== */
      .addCase(fetchSingleProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSingleProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedProduct = action.payload;
      })
      .addCase(fetchSingleProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== UPDATE ===== */
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        state.products = state.products.map((p) =>
          p._id === action.payload._id ? action.payload : p
        );

        state.vendorProducts = state.vendorProducts.map((p) =>
          p._id === action.payload._id ? action.payload : p
        );

        if (
          state.selectedProduct &&
          state.selectedProduct._id === action.payload._id
        ) {
          state.selectedProduct = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ===== DELETE ===== */
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.products = state.products.filter(
          (p) => p._id !== action.payload
        );
        state.vendorProducts = state.vendorProducts.filter(
          (p) => p._id !== action.payload
        );
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearProductState,
  clearSelectedProduct,
} = productSlice.actions;

export default productSlice.reducer;
